<template>
  <b-modal
    id="task-details"
    header-bg-variant="dark"
    header-text-variant="light"
  >
    <template v-slot:modal-header>
      <div class="d-flex align-items-center justify-content-between w-100">
        <h5 class="mb-0">{{task.task.title}}</h5>

        <b-badge variant="danger" v-if="isDelayed">
          Atrasada
        </b-badge>
      </div>
    </template>

    <div class="d-flex justify-content-center py-4" v-if="task.loading">
      <b-spinner variant="secondary"></b-spinner>
    </div>

    <b-row v-if="!task.loading">
      <b-col>
        <div>
          <span class="text-muted mr-2">Interesses:</span>
          {{task.task.interests.join(', ')}}
        </div>

        <div class="d-flex" v-if="task.task.phone || task.task.mobile">
          <span class="text-muted mr-2">Telefone(s):</span>
          <div v-if="task.task.phone">
            <a :href="`tel:55${unmaskPhone(task.task.phone)}`" target="_blank">
              {{task.task.phone}}
            </a>
          </div>

          <span v-if="task.task.phone && task.task.mobile" class="mx-2">
            /
          </span>

          <div v-if="task.task.mobile">
            <a :href="`https://wa.me/55${unmaskPhone(task.task.mobile)}`" target="_blank">
              {{task.task.mobile}}
            </a>
          </div>
        </div>

        <div>
          <span class="text-muted mr-2">Captação:</span>
          {{task.task.origin}}
        </div>
      </b-col>
    </b-row>

    <hr v-if="!task.loading">

    <b-row v-if="!task.loading">
      <b-col lg="6" class="mb-2 mb-lg-0" v-if="this.isAdmin">
        <label>
          Responsável
        </label>

        <b-form-select
          v-model="task.task.assignee_id"
          :options="users"
          :disabled="settings.isLoadingUsers"
          @change="updateAssignee"
        >
          <template v-slot:first>
            <b-select-option value="null">
              Nenhum
            </b-select-option>
          </template>
        </b-form-select>
      </b-col>

      <b-col :lg="(this.isAdmin) ? '6' : '12'">
        <label>
          Data de entrega
        </label>

        <b-form-datepicker
          :min="today"
          :value="dueDate"
          label-no-date-selected=""
          :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
          locale="pt-br"
          @input="updateDueDate"
        >
          <template v-slot:button-content>
            <small>
              <b-icon-calendar-2-check class="text-muted"></b-icon-calendar-2-check>
            </small>
          </template>
        </b-form-datepicker>
      </b-col>
    </b-row>

    <hr v-if="!task.loading">

    <b-row v-if="!task.loading">
      <b-col class="overflow-auto" style="max-height: 30vh" v-if="task.comments.length > 0">
        <b-card
          class="p-2 border-0 bg-light rounded"
          no-body
          bg-white
          v-for="(comment, index) in task.comments"
          :key="index"
        >
          <small class="text-muted mb-1">
            <strong>{{comment.author}}</strong> em {{comment.created_at}}
          </small>
          <p class="mb-0" v-html="comment.content"/>
        </b-card>
      </b-col>

      <b-col v-else>
        <div class="p-4 border-0 bg-light rounded text-center">
          Sem comentários
        </div>
      </b-col>
    </b-row>

    <hr>

    <wysiwyg v-model.trim="content" placeholder="Deixe um comentário" :disabled="task.loading"/>

    <template v-slot:modal-footer>
      <b-button
        variant="outline-secondary"
        size="sm"
        @click="close()"
      >
        Fechar
      </b-button>

      <b-button
        variant="primary"
        size="sm"
        :disabled="!content || task.loading"
        @click="comment()"
      >
        Comentar
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import DateParser from '@/parsers/date';

export default {
  data() {
    return {
      content: '',
    };
  },

  computed: {
    ...mapState(['task', 'settings']),

    users() {
      return (this.settings.users || [])
        .map(({ name, uid }) => ({ text: name, value: uid }));
    },

    isDelayed() {
      if (this.task.task.due_date) {
        return DateParser.isBeforeToday(this.task.task.due_date);
      }

      return false;
    },

    today() {
      return moment().format('YYYY-MM-DD');
    },

    dueDate: {
      get() {
        return moment(this.task.task.due_date, 'DD/MM/YYYY').format('YYYY-MM-DD');
      },

      set(key, value) {
        return value;
      },
    },
  },

  methods: {
    fetchUsers() {
      if (!this.settings.users) {
        this.$store.dispatch('settings/fetchUsers', this.user.company_id);
      }
    },

    unmaskPhone(phone) {
      return phone
        .replace('(', '')
        .replace(')', '')
        .replace('-', '')
        .replace(' ', '');
    },

    async updateAssignee(value) {
      try {
        const task = {
          id: this.task.task.id,
          status: this.task.task.status,
          assignee_id: this.task.task.assignee_id,
          assignee: this.users.filter((user) => user.value === value).pop().text,
        };

        await this.$store.dispatch('projects/updateTask', task);
        await this.$store.dispatch('task/updateCurrentTask', task);

        this.$emit('refresh', task);
      } catch (err) {
        console.log(err);
      }
    },

    async updateDueDate(date) {
      try {
        const timestamp = moment(date, 'YYYY-MM-DD').format('X');
        const task = {
          id: this.task.task.id,
          status: this.task.task.status,
          due_date: { seconds: parseInt(timestamp, 0) },
        };

        await this.$store.dispatch('projects/updateTask', task);
        // await this.$store.dispatch('task/updateCurrentTask', task);

        this.$emit('refresh', task);
      } catch (err) {
        console.log(err);
      }
    },

    comment() {
      const seconds = moment().unix();

      this.$store.dispatch('task/createComment', {
        created_at: { seconds },
        updated_at: { seconds },
        user_id: this.user.uid,
        task_id: this.task.task.id,
        content: this.content,
      });

      this.content = '';
    },

    close() {
      this.content = '';
      this.$bvModal.hide('task-details');
    },
  },
};
</script>
